import React, { useCallback, useContext, useMemo, useState } from 'react';
import get from 'lodash/get';
import { useSelector } from 'react-redux';

import { Button } from 'src/presentation/components/agent_desktop_components';
import Dropdown from 'src/presentation/components/forms/Dropdown';
import FormContext from 'src/presentation/contexts/FormContext';
import { Row, Section } from 'src/presentation/components/forms/Layout';
import { SidekickThreadEditorContext } from 'src/presentation/components/pages/sidekick/threads/SidekickThreadEditorContext';
import { selectCurrentThread } from 'src/presentation/components/pages/sidekick/threads/actions/threadsUiSlice';
import useCreateNewThread from 'src/presentation/components/pages/sidekick/threads/hooks/useCreateNewThread';
import useOpenThread from 'src/presentation/components/pages/sidekick/threads/hooks/useOpenThread';
import { useResourceValues } from 'src/presentation/hooks/useResource';
import sidekickFastThreadsResource from 'src/actions/sidekick/sidekickFastThreadsResource';

type PivotProps = {
  attr: string;
  onChange: (name: string) => void;
};

const Pivot = ({ attr, onChange }: PivotProps) => {
  const { workingState } = useContext(FormContext);
  const [value, setValue] = useState(get(workingState, attr, ''));

  const { root } = useContext(SidekickThreadEditorContext);
  const { newThreadModalRef } = useContext(SidekickThreadEditorContext);

  const currentThread = useSelector(selectCurrentThread);
  const { values: threads } = useResourceValues(sidekickFastThreadsResource);

  const options = useMemo(() => {
    const options = threads
      .filter(thread => thread.id !== currentThread.id)
      .map(thread => ({
        label: thread.name,
        value: thread.id,
      }));

    options.unshift({ label: 'Create a thread', value: '' });

    return options;
  }, [threads, currentThread]) as { label: string; value: string }[];

  const handleChanges = useCallback(
    value => {
      setValue(value);
      onChange(value);
    },
    [setValue, onChange]
  );

  const onNewThreadCreated = useCallback(
    lastCreatedThreadId => {
      handleChanges(lastCreatedThreadId);
    },
    [handleChanges]
  );

  const { show: showCreateNewThreadModal } = useCreateNewThread({
    newThreadModalRef,
    onNewThreadCreated,
  });
  const { handleOpenThread } = useOpenThread({ root });

  const handleOpenAction = useCallback(() => {
    if (!value) {
      showCreateNewThreadModal();
      return;
    }

    handleOpenThread(value);
  }, [handleOpenThread, value, showCreateNewThreadModal]);

  const buttonLabel = value ? 'Edit' : 'Create';

  return (
    <Section>
      <Row>
        <Dropdown
          allowEmptyValues
          attr={attr}
          data-aid="pivot-dropdown"
          onChange={handleChanges}
          options={options}
          placeholder="Create a Thread"
          searchable
        />
        <Button onClick={handleOpenAction}>{buttonLabel}</Button>
      </Row>
    </Section>
  );
};

export default Pivot;
